import axios from 'axios';
import { useApiConfigStore } from '@store/api_config';

const publicClient = axios.create();

publicClient.interceptors.request.use((config) => {
  config.baseURL = useApiConfigStore.getState().getApiUrl();
  return config;
});

export interface TrackingData {
  ordre_num: string;
  org_id?: string;
  org_name: string;
  org_address?: string;
  org_phone?: string;
  org_email?: string;
  org_whatsapp_phone?: string;
  tracking_step: string;
  has_carte_grise: boolean;
  carte_grise_name?: string;
  vehicle_plate?: string;
  vehicle_marque?: string;
  vehicle_modele?: string;
  vehicle_coloris?: string;
  service_type?: string;
  intervention_type?: string;
  interventions?: string[];
  deposit_date?: string;
  restitution_date?: string;
  mobility_option?: string;
  tire_option?: string;
  client_email?: string;
  client_km?: number;
  last_km?: number;
  client_comments?: string;
  client_address?: string;
  client_intervention_detail?: string;
  client_rating?: number;
  devis_pdf_url?: string;
  devis_sign_token?: string;
  devis_sign_status?: string;
  devis_estimate_token?: string;
  devis_requested?: boolean;
  devis_signed?: boolean;
  devis_supplementaire_requested?: boolean;
  initial_devis_signed?: boolean;
  additional_devis_signed?: boolean;
  initial_devis_token?: string;
  additional_devis_token?: string;
  additional_devis_groups?: {
    key: string;
    signed: boolean;
    token?: string;
  }[];
  payment_link_url?: string;
  acompte_requested?: boolean;
  acompte_paid?: boolean;
  solde_requested?: boolean;
  solde_paid?: boolean;
  facture_pdf_url?: string;
  facture_available?: boolean;
  google_review_url?: string;
}

export const getTrackingData = (ordreId: string): Promise<TrackingData> =>
  publicClient.get(`/ordres/${ordreId}/tracking`).then((r) => r.data);

export const submitTrackingRating = (ordreId: string, rating: number): Promise<void> =>
  publicClient.post(`/ordres/${ordreId}/tracking/rating`, { rating });

export interface TrackingCompletionPayload {
  mobility_option?: string;
  tire_option?: string;
  email?: string;
  km?: number;
  comments?: string;
  address?: string;
  intervention_detail?: string;
}

export interface PublicIntervention {
  id: string;
  name: string;
  type: string;
  active: boolean;
}

export const getPublicInterventions = (orgId: string): Promise<PublicIntervention[]> =>
  publicClient.get(`/public/org/${orgId}/interventions`).then((r) => r.data);


export const submitTrackingCompletion = (ordreId: string, payload: TrackingCompletionPayload): Promise<void> =>
  publicClient.post(`/ordres/${ordreId}/tracking/complete`, payload);

export const uploadCarteGrise = (ordreId: string, file: File): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file);
  return publicClient.post(`/ordres/${ordreId}/tracking/carte-grise`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export interface CarteGriseValidationResult {
  valid: boolean;
  is_carte_grise: boolean;
  is_readable: boolean;
  immatriculation_lue?: string | null;
  immatriculation_attendue?: string | null;
  immatriculation_match?: boolean | null;
  message: string;
}

export const validateCarteGrise = (file: File, ordreId?: string): Promise<CarteGriseValidationResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const params = ordreId ? `?ordre_id=${ordreId}` : '';
  return publicClient.post(`/public/validate-carte-grise${params}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);
};
